import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Copy, Eye, EyeOff, Pencil, Trash2, Share2, Star, KeyRound, ExternalLink, ShieldCheck, Tag, Upload, Layers,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import VaultItemDialog, { CATEGORIES } from "@/components/VaultItemDialog";
import ImportDialog from "@/components/ImportDialog";
import DuplicatesDialog from "@/components/DuplicatesDialog";
import ShareDialog from "@/components/ShareDialog";
import PasswordGenerator from "@/components/PasswordGenerator";
import { BulkSelectionBar } from "@/components/BulkSelectionBar";
import { BulkDeleteDialog } from "@/components/BulkDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [revealed, setRevealed] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dupesOpen, setDupesOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vault");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load vault");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const currentIds = new Set(items.map((item) => item.id));
    setSelectedIds((previous) => new Set([...previous].filter((id) => currentIds.has(id))));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (category !== "All" && (it.category || "Personal") !== category) return false;
      if (!q) return true;
      const hay = `${it.title} ${it.username} ${it.url} ${(it.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, category]);

  const favoriteCount = useMemo(() => items.filter((i) => i.is_favorite).length, [items]);
  const visibleIds = useMemo(() => filtered.map((item) => item.id), [filtered]);
  const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const openCreate = () => { setEditingItem(null); setDialogOpen(true); };
  const openEdit = (it) => { setEditingItem(it); setDialogOpen(true); };
  const openShare = (it) => { setShareItem(it); setShareOpen(true); };

  const onSaved = () => { load(); };

  const toggleReveal = (id) => setRevealed((r) => ({ ...r, [id]: !r[id] }));

  const toggleSelected = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const copyValue = async (val, label) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Unable to copy");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/vault/${deleteId}`);
      toast.success("Item deleted");
      setItems((arr) => arr.filter((i) => i.id !== deleteId));
      setSelectedIds((previous) => {
        const next = new Set(previous);
        next.delete(deleteId);
        return next;
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  const confirmBulkDelete = async () => {
    const itemIds = [...selectedIds];
    if (!itemIds.length || bulkDeleting) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/vault/bulk-delete", { item_ids: itemIds });
      const deletedIds = new Set(itemIds);
      setItems((current) => current.filter((item) => !deletedIds.has(item.id)));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      toast.success(`${data.deleted} ${data.deleted === 1 ? "item" : "items"} deleted`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-7" data-testid="vault-dashboard">
      {/* Hero / Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.08em] text-[color:var(--vault-text-tertiary)]">Welcome back</p>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl tracking-tight font-semibold mt-1">
            Hi, {user?.name?.split(" ")[0] || "friend"}
          </h1>
          <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-xl">
            Your vault holds <span className="font-medium text-[color:var(--vault-brand)]">{items.length}</span> item{items.length === 1 ? "" : "s"}. Stored safely, encrypted at rest.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PasswordGenerator />
          <Button
            onClick={() => setImportOpen(true)}
            variant="outline"
            className="rounded-xl border-[color:var(--vault-border)]"
            data-testid="import-vault-btn"
          >
            <Upload size={18} className="mr-1.5" />
            Import
          </Button>
          <Button
            onClick={() => setDupesOpen(true)}
            variant="outline"
            className="rounded-xl border-[color:var(--vault-border)]"
            data-testid="find-duplicates-btn"
          >
            <Layers size={18} className="mr-1.5" />
            Find duplicates
          </Button>
          <Button
            onClick={openCreate}
            className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
            data-testid="add-vault-item-btn"
          >
            <Plus size={18} className="mr-1.5" />
            New item
          </Button>
        </div>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={KeyRound} label="Passwords" value={items.filter(i => i.type === "login" || !i.type).length} accent="var(--vault-brand)" testid="stat-passwords" />
        <StatCard icon={Star} label="Favorites" value={favoriteCount} accent="#DD6B20" testid="stat-favorites" />
        <StatCard icon={ShieldCheck} label="Categories used" value={new Set(items.map(i => i.category || "Personal")).size} accent="#38A169" testid="stat-categories" />
      </div>

      {/* Filters */}
      <div className="vault-card p-4 md:p-5">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--vault-text-tertiary)]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, username, url, or #tag"
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] pl-9"
              data-testid="vault-search-input"
            />
          </div>
          <Tabs value={category} onValueChange={setCategory} className="md:flex">
            <TabsList className="bg-[#E6EEF8] rounded-xl overflow-x-auto max-w-full">
              <TabsTrigger value="All" data-testid="cat-tab-All">All</TabsTrigger>
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c} value={c} data-testid={`cat-tab-${c}`}>{c}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <BulkSelectionBar
          visibleCount={visibleIds.length}
          selectedCount={selectedIds.size}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
          onToggleAll={toggleAllVisible}
          onClear={() => setSelectedIds(new Set())}
          onDelete={() => setBulkDeleteOpen(true)}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-[color:var(--vault-text-tertiary)]">Loading your vault…</div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={openCreate} hasItems={items.length > 0} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="vault-items-grid">
          <AnimatePresence>
            {filtered.map((it, idx) => (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, delay: Math.min(idx * 0.02, 0.2) }}
                className="vault-card vault-card-hover p-5"
                data-testid={`vault-item-${it.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <Checkbox
                    checked={selectedIds.has(it.id)}
                    onCheckedChange={() => toggleSelected(it.id)}
                    aria-label={`Select ${it.title}`}
                    className="mt-1"
                    data-testid={`select-vault-item-${it.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-[Outfit] text-lg font-medium truncate" data-testid="vault-item-title">{it.title}</h3>
                      {it.is_favorite && <Star size={14} className="text-[color:var(--vault-warning)] fill-current" />}
                    </div>
                    {it.username && (
                      <div className="text-sm text-[color:var(--vault-text-secondary)] truncate mt-0.5">
                        {it.username}
                      </div>
                    )}
                    {it.url && (
                      <a
                        href={it.url.startsWith("http") ? it.url : `https://${it.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[color:var(--vault-info)] hover:underline mt-1"
                        data-testid="vault-item-url"
                      >
                        <ExternalLink size={12} />
                        <span className="truncate max-w-[180px]">{it.url}</span>
                      </a>
                    )}
                  </div>
                  <span className="pill shrink-0">{it.category || "Personal"}</span>
                </div>

                <div className="mt-4 rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] px-3 py-2 flex items-center justify-between gap-2">
                  <div className="font-mono text-sm tracking-wider truncate text-[color:var(--vault-text-primary)]" data-testid={`vault-item-password-${it.id}`}>
                    {(() => {
                      const secret = it.type === "note" && !it.password ? it.notes : it.password;
                      if (!secret) return "—";
                      return revealed[it.id] ? secret : "•".repeat(Math.min(14, Math.max(8, secret.length)));
                    })()}
                  </div>
                  <div className="flex shrink-0">
                    <IconBtn onClick={() => toggleReveal(it.id)} title={revealed[it.id] ? "Hide" : "Reveal"} testid={`reveal-btn-${it.id}`}>
                      {revealed[it.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                    </IconBtn>
                    <IconBtn onClick={() => (it.type === "note" && !it.password ? copyValue(it.notes, "Note") : copyValue(it.password, "Password"))} title="Copy" testid={`copy-pw-btn-${it.id}`}>
                      <Copy size={15} />
                    </IconBtn>
                  </div>
                </div>

                {it.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {it.tags.map((t) => (
                      <span key={t} className="pill" data-testid={`tag-${t}`}>
                        <Tag size={10} /> {t}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="text-[11px] text-[color:var(--vault-text-tertiary)]">
                    Updated {formatDate(it.updated_at)}
                  </div>
                  <div className="flex">
                    <IconBtn onClick={() => copyValue(it.username, "Username")} title="Copy username" testid={`copy-user-btn-${it.id}`}>
                      <Copy size={14} />
                    </IconBtn>
                    <IconBtn onClick={() => openShare(it)} title="Share" testid={`share-btn-${it.id}`}>
                      <Share2 size={14} />
                    </IconBtn>
                    <IconBtn onClick={() => openEdit(it)} title="Edit" testid={`edit-btn-${it.id}`}>
                      <Pencil size={14} />
                    </IconBtn>
                    <IconBtn onClick={() => setDeleteId(it.id)} title="Delete" danger testid={`delete-btn-${it.id}`}>
                      <Trash2 size={14} />
                    </IconBtn>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <VaultItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        item={editingItem}
        onSaved={onSaved}
      />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} item={shareItem} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <DuplicatesDialog open={dupesOpen} onOpenChange={setDupesOpen} onMerged={load} />
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={(open) => !bulkDeleting && setBulkDeleteOpen(open)}
        count={selectedIds.size}
        deleting={bulkDeleting}
        onConfirm={confirmBulkDelete}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the vault entry and any sharing on it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-xl bg-[color:var(--vault-error)] hover:bg-[#b91c1c] text-white"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="vault-card p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: accent }}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">{label}</div>
        <div className="font-[Outfit] text-2xl font-semibold">{value}</div>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 rounded-lg transition-colors ${danger
        ? "text-[color:var(--vault-error)] hover:bg-[color:var(--vault-error-bg)]"
        : "text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"}`}
    >
      {children}
    </button>
  );
}

function EmptyState({ onCreate, hasItems }) {
  return (
    <div className="vault-card p-12 text-center" data-testid="vault-empty-state">
      <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: "var(--vault-gradient)" }}>
        <KeyRound size={28} />
      </div>
      <h3 className="font-[Outfit] text-2xl mt-4 font-medium">
        {hasItems ? "No matches" : "Your vault is empty"}
      </h3>
      <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-md mx-auto">
        {hasItems
          ? "Try clearing the search or selecting a different category."
          : "Add your first password to get started — only you can read it."}
      </p>
      {!hasItems && (
        <Button onClick={onCreate} className="mt-5 rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="empty-add-btn">
          <Plus size={16} className="mr-1.5" /> Add your first item
        </Button>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}
